import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { ImageWithDate } from '../../../../types/contactLog';
import {
  ArrowLeftIcon,
  CheckCircleIcon,
  CircleIcon,
  DownloadIcon,
} from '../../../../components/Icons';
import { PhotoDetailModal } from './PhotoDetailModal';
import { canUseShareSheet, isAbortError, prefetchFiles, savePrefetchedPhotos } from './photoSave';
import {
  Overlay,
  Header,
  BackButton,
  Title,
  Grid,
  Cell,
  Checkbox,
  SaveBar,
  SaveBarButton,
  FADE_DURATION_MS,
} from './AllPhotosModal.styled';

interface AllPhotosModalProps {
  isOpen: boolean;
  onClose: () => void;
  images: ImageWithDate[];
}

// 選択状態を識別するキー（s3_key 優先、無ければ url）
const imageKey = (image: ImageWithDate): string => image.s3_key ?? image.url;

// 長押し判定の閾値（ms）
const LONG_PRESS_MS = 500;

// 選択済み写真の先読み状態。ready になるまで保存ボタンを押させない。
// seq は取得処理の同一性を照合するための世代番号（選び直しで発火し直した取得と区別する）
type PrepareState =
  | { status: 'pending'; seq: number }
  | { status: 'ready'; seq: number; file: File }
  | { status: 'failed'; seq: number };

const AllPhotosModalComponent: React.FC<AllPhotosModalProps> = ({ isOpen, onClose, images }) => {
  // fadeOut を見せてからアンマウントするためのフラグ
  const [isClosing, setIsClosing] = useState(false);
  // タップされた写真（詳細オーバーレイの表示対象）。null で非表示
  const [selectedImage, setSelectedImage] = useState<ImageWithDate | null>(null);
  // 長押しで入る選択モード
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  // 選択済み写真のキー集合
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [isSaving, setIsSaving] = useState(false);
  // 選択済み写真の先読み結果（キーは imageKey）
  const [prepared, setPrepared] = useState<Map<string, PrepareState>>(new Map());

  // 先読みの世代番号。同じ写真を選び直すと採番し直す
  const prepareSeqRef = useRef(0);
  // 進行中の先読みを打ち切るためのコントローラ（キーは imageKey）
  const prefetchControllersRef = useRef<Map<string, AbortController>>(new Map());

  // 長押し検出用タイマー。発火したら選択モードへ
  const longPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // 長押しが発火したかどうか（発火直後の onClick を無視するため）
  const longPressFiredRef = useRef(false);

  // fadeOut アニメーション終了後に実際に閉じる
  const handleClose = useCallback(() => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      onClose();
    }, FADE_DURATION_MS);
  }, [onClose]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    },
    [handleClose]
  );

  useEffect(() => {
    if (!isOpen) return;
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, handleKeyDown]);

  // 不要になった先読みを打ち切る。cache: 'reload' で毎回フル通信になるため、
  // 捨てる前提の取得を走らせ続けない
  const abortPrefetch = useCallback((key: string) => {
    const controller = prefetchControllersRef.current.get(key);
    if (!controller) return;
    controller.abort();
    prefetchControllersRef.current.delete(key);
  }, []);

  const abortAllPrefetches = useCallback(() => {
    prefetchControllersRef.current.forEach((controller) => controller.abort());
    prefetchControllersRef.current.clear();
  }, []);

  // アンマウント時も進行中の取得を残さない
  useEffect(() => abortAllPrefetches, [abortAllPrefetches]);

  // モーダルを閉じたら選択状態をリセット
  useEffect(() => {
    if (!isOpen) {
      setIsSelectionMode(false);
      setSelectedKeys(new Set());
      setPrepared(new Map());
      abortAllPrefetches();
    }
  }, [isOpen, abortAllPrefetches]);

  const clearLongPressTimer = useCallback(() => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  }, []);

  // 長押し開始: 一定時間押し続けたら選択モードに入り、その写真を選択する。
  // 共有シートが使えない環境では複数枚をまとめて渡せず一括保存が成立しないため、
  // 選択モードに入らせない（できるはずと誤認させない）
  const handlePressStart = useCallback(
    (image: ImageWithDate) => {
      if (!canUseShareSheet()) return;
      longPressFiredRef.current = false;
      clearLongPressTimer();
      longPressTimerRef.current = setTimeout(() => {
        longPressFiredRef.current = true;
        setIsSelectionMode(true);
        setSelectedKeys(new Set([imageKey(image)]));
      }, LONG_PRESS_MS);
    },
    [clearLongPressTimer]
  );

  // 押下中にモーダルが閉じた/アンマウントされた場合も長押しタイマーを破棄し、
  // クローズ後に選択モードへ入ってしまう副作用を防ぐ
  useEffect(() => {
    if (!isOpen) clearLongPressTimer();
    return () => clearLongPressTimer();
  }, [isOpen, clearLongPressTimer]);

  // 選択トグル。0枚になったら選択モードを解除する
  const toggleSelect = useCallback((image: ImageWithDate) => {
    const key = imageKey(image);
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      if (next.size === 0) setIsSelectionMode(false);
      return next;
    });
    // 選択を外したら進行中の取得を打ち切り、先読み結果を破棄する。File を保持し続けない
    // ための解放と、失敗したまま固定されず選び直しで再取得できるようにするための両方を兼ねる。
    // 選択を追加する側では対象が存在しないため、どちらも no-op になる
    abortPrefetch(key);
    setPrepared((prev) => {
      if (!prev.has(key)) return prev;
      const next = new Map(prev);
      next.delete(key);
      return next;
    });
  }, [abortPrefetch]);

  // セルのタップ: 選択モードならトグル、通常時は詳細を開く
  const handleCellClick = useCallback(
    (image: ImageWithDate) => {
      // 長押しで選択モードに入った直後のクリックは無視
      if (longPressFiredRef.current) {
        longPressFiredRef.current = false;
        return;
      }
      if (isSelectionMode) {
        toggleSelect(image);
      } else {
        setSelectedImage(image);
      }
    },
    [isSelectionMode, toggleSelect]
  );

  // 日付の新しい順にソート（date未設定の画像は末尾へ）
  const sortedImages = useMemo(() => {
    return [...images].sort((a, b) => {
      if (!a.date) return 1;
      if (!b.date) return -1;
      return b.date.localeCompare(a.date);
    });
  }, [images]);

  // 世代番号が一致するときだけ書き戻す。打ち切り機構の代わりで、次の2つを同時に防ぐ。
  // (1) モーダルを閉じて対応表を空にした後や選択解除後に解決した先読みがエントリを復活させる
  // (2) 選び直しで発火した新しい取得の結果を、遅れて解決した古い取得が上書きする
  const writePrepared = useCallback((key: string, seq: number, state: PrepareState) => {
    setPrepared((prev) => (prev.get(key)?.seq === seq ? new Map(prev).set(key, state) : prev));
  }, []);

  // 共有シートは transient activation を要求するため、選択された時点で File を用意しておく。
  // 共有シートに渡さない環境では取得しても使い道がないので、選択の入口ガードとは別に
  // ここでも環境を見る（選択を作る経路が増えても無駄な通信が復活しないようにする）
  useEffect(() => {
    if (!canUseShareSheet()) return;
    const targets = sortedImages.filter(
      (image) => selectedKeys.has(imageKey(image)) && !prepared.has(imageKey(image))
    );
    if (targets.length === 0) return;

    const seqByKey = new Map<string, number>();
    targets.forEach((image) => {
      prepareSeqRef.current += 1;
      seqByKey.set(imageKey(image), prepareSeqRef.current);
    });

    setPrepared((prev) => {
      const next = new Map(prev);
      targets.forEach((image) => {
        const key = imageKey(image);
        next.set(key, { status: 'pending', seq: seqByKey.get(key) as number });
      });
      return next;
    });

    targets.forEach((image) => {
      const key = imageKey(image);
      const seq = seqByKey.get(key) as number;
      const controller = new AbortController();
      prefetchControllersRef.current.set(key, controller);
      prefetchFiles([image], controller.signal)
        .then(([file]) => {
          // 共有シートに渡せるかは File を得てからでないと判定できない。詳細画面と同じ
          // 基準で判定し、渡せない環境では保存を試みずに案内へ倒す
          if (!navigator.canShare?.({ files: [file] })) {
            writePrepared(key, seq, { status: 'failed', seq });
            return;
          }
          writePrepared(key, seq, { status: 'ready', seq, file });
        })
        .catch((err) => {
          // 中断は打ち切りなので記録しない。失敗は長押し保存へ案内するため通知もしない
          if (isAbortError(err)) return;
          console.error('写真の先読みに失敗しました', err);
          writePrepared(key, seq, { status: 'failed', seq });
        })
        .finally(() => {
          // 同じキーで新しい取得が始まっている場合、そちらのコントローラは消さない
          if (prefetchControllersRef.current.get(key) === controller) {
            prefetchControllersRef.current.delete(key);
          }
        });
    });
  }, [selectedKeys, sortedImages, prepared, writePrepared]);

  // 選択済みのどれかが先読み未完了のあいだは保存させない
  const isPreparing = useMemo(
    () =>
      [...selectedKeys].some((key) => {
        const state = prepared.get(key);
        return state === undefined || state.status === 'pending';
      }),
    [selectedKeys, prepared]
  );

  // 選択モードを終了して後片付けする。先読み結果も破棄して File を保持し続けない
  const finishSelection = useCallback((keys: string[]) => {
    setPrepared((prev) => {
      const next = new Map(prev);
      keys.forEach((key) => next.delete(key));
      return next;
    });
    setSelectedKeys(new Set());
    setIsSelectionMode(false);
  }, []);

  // 選択済み写真を一括保存
  const handleSaveSelected = useCallback(async () => {
    if (isSaving || isPreparing || selectedKeys.size === 0) return;
    const targets = sortedImages.filter((image) => selectedKeys.has(imageKey(image)));
    const files = targets
      .map((image) => prepared.get(imageKey(image)))
      .filter(
        (state): state is { status: 'ready'; seq: number; file: File } => state?.status === 'ready'
      )
      .map((state) => state.file);
    setIsSaving(true);
    try {
      // 先読みに失敗した写真が混ざる場合は共有シートに渡さず、フォールバック経路に委ねる
      // savePrefetchedPhotos は共有シート呼び出しまで await を挟まない。前段に await を置かないこと
      const outcome = await savePrefetchedPhotos(
        files.length === targets.length ? files : null,
        targets
      );
      // キャンセルは失敗ではない。選択と先読み結果を維持してそのままやり直せるようにする
      // （破棄すると全部選び直し + 全部再ダウンロードになる）
      if (outcome === 'canceled') return;

      // 保存を試みた後は成功・不成立のどちらでも選択モードを抜ける。不成立時は詳細を
      // 開いて長押し保存してもらう動線に案内するため、選択モードのままでは案内どおりに
      // タップしても選択トグルになり写真を開けない
      finishSelection(targets.map(imageKey));

      if (outcome !== 'shared') {
        // 長押し保存は1枚ずつしかできないため、詳細を開いてもらう動線に案内する
        alert(
          outcome === 'needsOneByOne'
            ? '写真を1枚ずつ開いて、写真長押しで保存してください。'
            : '写真を開いて、写真長押しで保存してください。'
        );
      }
    } catch (err) {
      console.error('写真の保存に失敗しました', err);
      alert('写真の保存に失敗しました。時間をおいて再度お試しください。');
    } finally {
      setIsSaving(false);
    }
  }, [isSaving, isPreparing, selectedKeys, sortedImages, prepared, finishSelection]);

  // fadeOut を再生し終えるまではアンマウントしない
  if (!isOpen && !isClosing) return null;

  return createPortal(
    <Overlay
      $isClosing={isClosing}
      role="dialog"
      aria-modal="true"
      aria-labelledby="all-photos-title"
    >
      <Header>
        <BackButton onClick={handleClose} aria-label="戻る">
          <ArrowLeftIcon />
        </BackButton>
        <Title id="all-photos-title">全ての写真</Title>
      </Header>
      <Grid $selectionMode={isSelectionMode}>
        {sortedImages.map((image, index) => {
          const key = image.s3_key ?? image.url ?? String(index);
          const checked = selectedKeys.has(imageKey(image));
          return (
            <Cell
              key={key}
              $imageUrl={image.url}
              role="button"
              tabIndex={0}
              aria-label={isSelectionMode ? '写真の選択を切り替え' : '写真を拡大表示'}
              aria-pressed={isSelectionMode ? checked : undefined}
              onClick={() => handleCellClick(image)}
              onKeyDown={(e) => {
                // キーボード操作でも詳細表示/選択トグルできるよう Enter/Space に対応
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleCellClick(image);
                }
              }}
              onTouchStart={() => handlePressStart(image)}
              onTouchEnd={clearLongPressTimer}
              onTouchMove={clearLongPressTimer}
              onMouseDown={() => handlePressStart(image)}
              onMouseUp={clearLongPressTimer}
              onMouseLeave={clearLongPressTimer}
              onContextMenu={(e) => e.preventDefault()}
            >
              {isSelectionMode && (
                <Checkbox>{checked ? <CheckCircleIcon /> : <CircleIcon />}</Checkbox>
              )}
            </Cell>
          );
        })}
      </Grid>
      <SaveBar $visible={selectedKeys.size > 0}>
        <SaveBarButton onClick={handleSaveSelected} disabled={isSaving || isPreparing}>
          <DownloadIcon />
          {/* 保存後の SaveBar スライドアウト中は選択が 0 になるため、枚数は 1 枚以上のときだけ出す */}
          {isPreparing
            ? '準備中…'
            : selectedKeys.size > 0
              ? `${selectedKeys.size}枚を保存する`
              : '写真を保存する'}
        </SaveBarButton>
      </SaveBar>
      <PhotoDetailModal image={selectedImage} onClose={() => setSelectedImage(null)} />
    </Overlay>,
    document.body
  );
};

export const AllPhotosModal = React.memo(AllPhotosModalComponent);

export default AllPhotosModal;
